import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, onSnapshot, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Images, Film, Plus, Trash2, Edit3, Save,
  ExternalLink, Eye, Clock, MessageCircle,
  Camera, Sparkles, Check, X,
  AlertCircle, Link, ArrowLeft
} from 'lucide-react';

// ── FIREBASE ────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBV0W6ytsKw5uU32IZn2sOSp8IjwmT0nvs",
  authDomain: "wedding-quote-app.firebaseapp.com",
  projectId: "wedding-quote-app",
  storageBucket: "wedding-quote-app.appspot.com",
  messagingSenderId: "1019978209954",
  appId: "1:1019978209954:web:2ce6ebdb1b42c0b7d495fb"
};
const firebaseApp = initializeApp(firebaseConfig, 'client-portal');
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const ADMIN_CODE = 'wayssmffss2';
const LOGO_URL = "https://thesparkstudios.ca/wp-content/uploads/2025/01/logo@2x.png";
const WHATSAPP = "16478633135";
const APP_ID = 'spark-client-portal';

// ── STYLES ──────────────────────────────────────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=Montserrat:wght@100..900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Montserrat', sans-serif; background: #0a0a0a; -webkit-font-smoothing: antialiased; }
  .font-serif { font-family: 'Cormorant Garamond', serif !important; }
  .font-sans  { font-family: 'Montserrat', sans-serif !important; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes pulse-gold {
    0%   { box-shadow: 0 0 0 0   rgba(197,160,89,.45); }
    70%  { box-shadow: 0 0 0 16px rgba(197,160,89,0); }
    100% { box-shadow: 0 0 0 0   rgba(197,160,89,0); }
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .fade-up   { animation: fadeUp .6s ease both; }
  .fade-up-2 { animation: fadeUp .6s .12s ease both; }
  .fade-up-3 { animation: fadeUp .6s .24s ease both; }
  .fade-up-4 { animation: fadeUp .6s .36s ease both; }

  .gold-shimmer {
    background: linear-gradient(90deg, #C5A059 0%, #e8c97a 40%, #C5A059 60%, #a07830 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shimmer 3s linear infinite;
  }
  .pulse-gold { animation: pulse-gold 2s infinite; }
  .spin { animation: spin .8s linear infinite; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #111; }
  ::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }

  input, textarea, select {
    font-family: 'Montserrat', sans-serif;
  }
  input::placeholder, textarea::placeholder { color: #555; }
`;

// ── COLOUR / SPACING TOKENS ──────────────────────────────────────────────────
const C = {
  gold:    '#C5A059',
  goldHov: '#d4b06a',
  bg:      '#0a0a0a',
  surface: '#111111',
  card:    '#161616',
  border:  '#222222',
  muted:   '#333333',
  text:    '#f0ece4',
  textSub: '#888888',
  white:   '#ffffff',
};

// ── HELPERS ──────────────────────────────────────────────────────────────────
// Hero images are full-bleed banners, not print files — downscaling before
// upload keeps Storage size/bandwidth down and the client's page load fast.
const HEIC_PATTERN = /\.(heic|heif)$/i;
const isHeic = (file) => HEIC_PATTERN.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';

const resizeImage = (file, maxDim = 1920, quality = 0.82, timeoutMs = 15000) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    const timer = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Timed out reading that photo — try a different one, or save it as JPG/PNG first.'));
    }, timeoutMs);
    const cleanup = () => { clearTimeout(timer); URL.revokeObjectURL(objectUrl); };
    img.onload = () => {
      cleanup();
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) { height = Math.round(height * (maxDim / width)); width = maxDim; }
        else { width = Math.round(width * (maxDim / height)); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not process image'))), 'image/jpeg', quality);
    };
    img.onerror = () => { cleanup(); reject(new Error('Could not read that photo — try a different one, or save it as JPG/PNG first.')); };
    img.src = objectUrl;
  });

const timeAgo = (ts) => {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)   return 'Just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400)return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

// ── INITIAL GALLERY TEMPLATE ─────────────────────────────────────────────────
const blankGallery = () => ({
  clientName: '',
  weddingDate: '',
  heroImage: '',
  coverNote: '',
  accessCode: '',
  isActive: true,
  createdAt: Date.now(),
  lastViewedAt: null,
  views: 0,
  sections: [
    { id: Date.now()+1, type: 'photos',  label: 'Wedding Photos',      url: '', note: '', status: 'ready' },
    { id: Date.now()+2, type: 'video',   label: 'Highlight Film',      url: '', note: '', status: 'editing' },
    { id: Date.now()+3, type: 'video',   label: 'Full Documentary',    url: '', note: '', status: 'pending' },
  ]
});

// ══════════════════════════════════════════════════════════════════════════════
//  ROOT APP
// ══════════════════════════════════════════════════════════════════════════════
export default function ClientPortalApp() {
  const [fbReady,   setFbReady]   = useState(false);
// eslint-disable-next-line no-unused-vars
const [isAdmin] = useState(false);
  const [clientCode,setClientCode]= useState('');
  const [gallery,   setGallery]   = useState(null);   // loaded client gallery
  const [view,      setView]      = useState('lock');  // lock | client | admin | admin-edit

  // Check hash for direct client link
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#/portal/')) {
      const code = hash.replace('#/portal/', '');
      setClientCode(code);
      setView('client-loading');
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) await signInAnonymously(auth);
      setFbReady(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!fbReady || view !== 'client-loading' || !clientCode) return;
    loadClientGallery(clientCode);
  }, [fbReady, view, clientCode]);

  const loadClientGallery = async (code) => {
    try {
      const snap = await getDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', code));
      if (snap.exists()) {
        const data = snap.data();
        setGallery(data);
        // record view
        await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', code), {
          views: (data.views || 0) + 1,
          lastViewedAt: Date.now()
        });
        setView('client');
      } else {
        setView('not-found');
      }
    } catch (e) {
      setView('not-found');
    }
  };

  if (!fbReady) return <Splash />;
  if (view === 'client-loading') return <Splash label="Opening your gallery…" />;
  if (view === 'not-found') return <NotFound />;
  if (view === 'lock')   return <LockScreen onAdmin={() => setView('admin')} onClient={(code) => { setClientCode(code); setView('client-loading'); }} />;
  if (view === 'client') return <ClientView gallery={gallery} code={clientCode} />;
  if (view === 'admin')  return <AdminDashboard onEdit={(g, id) => { setGallery(g); setClientCode(id); setView('admin-edit'); }} />;
  if (view === 'admin-edit') return <GalleryEditor galleryId={clientCode} initial={gallery} onBack={() => setView('admin')} onSaved={(g) => setGallery(g)} />;
  return null;
}

// ── SPLASH ───────────────────────────────────────────────────────────────────
function Splash({ label = 'The Spark Studios' }) {
  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:20 }}>
      <style>{GLOBAL_STYLES}</style>
      <img src={LOGO_URL} alt="Spark Studios" style={{ height:40, opacity:.7, filter:'brightness(2)' }} />
      <p style={{ color:C.textSub, fontSize:10, letterSpacing:'0.4em', textTransform:'uppercase', fontFamily:'Montserrat,sans-serif' }}>{label}</p>
    </div>
  );
}

// ── NOT FOUND ────────────────────────────────────────────────────────────────
function NotFound() {
  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ textAlign:'center', color:C.text }}>
        <AlertCircle size={40} style={{ color:C.gold, marginBottom:16 }} />
        <h2 className="font-serif" style={{ fontSize:36, marginBottom:8, color:C.white }}>Gallery Not Found</h2>
        <p style={{ color:C.textSub, fontSize:12, letterSpacing:'0.2em' }}>The access code may be incorrect or this gallery has been removed.</p>
      </div>
    </div>
  );
}

// ── LOCK SCREEN ──────────────────────────────────────────────────────────────
function LockScreen({ onAdmin, onClient }) {
  const [code, setCode] = useState('');
  const [err,  setErr]  = useState('');
  const [tab,  setTab]  = useState('client'); // client | admin

  const handleSubmit = () => {
    setErr('');
    if (!code.trim()) return;
    if (tab === 'admin') {
      if (code === ADMIN_CODE) { onAdmin(); }
      else setErr('Incorrect studio code.');
    } else {
      onClient(code.trim().toLowerCase());
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:24, position:'relative', overflow:'hidden' }}>
      <style>{GLOBAL_STYLES}</style>
      {/* bg grain */}
      <div style={{ position:'absolute', inset:0, backgroundImage:'url(https://www.transparenttextures.com/patterns/carbon-fibre.png)', opacity:.04, pointerEvents:'none' }} />
      {/* gold orb */}
      <div style={{ position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width:400, height:400, background:`radial-gradient(circle, ${C.gold}22 0%, transparent 70%)`, pointerEvents:'none' }} />

      <div className="fade-up" style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:40, padding:'52px 48px', maxWidth:440, width:'100%', position:'relative' }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <img src={LOGO_URL} alt="Spark Studios" style={{ height:36, filter:'brightness(2)', marginBottom:20 }} />
          <h1 className="font-serif gold-shimmer" style={{ fontSize:32, fontWeight:300, letterSpacing:'.02em', display:'block', marginBottom:6 }}>Client Portal</h1>
          <p style={{ color:C.textSub, fontSize:10, letterSpacing:'0.45em', textTransform:'uppercase' }}>Your wedding · delivered</p>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', background:C.card, borderRadius:16, padding:4, marginBottom:28, border:`1px solid ${C.border}` }}>
          {['client','admin'].map(t => (
            <button key={t} onClick={() => { setTab(t); setErr(''); setCode(''); }}
              style={{ flex:1, padding:'10px 0', borderRadius:12, border:'none', cursor:'pointer', fontSize:10, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em', textTransform:'uppercase', transition:'all .2s',
                background: tab===t ? C.gold : 'transparent',
                color: tab===t ? C.bg : C.textSub }}>
              {t === 'client' ? 'My Gallery' : 'Studio Admin'}
            </button>
          ))}
        </div>

        <p style={{ color:C.textSub, fontSize:11, marginBottom:14, letterSpacing:'0.15em' }}>
          {tab === 'client' ? 'Enter the access code from your delivery email' : 'Studio internal access only'}
        </p>

        <input
          type={tab === 'admin' ? 'password' : 'text'}
          value={code}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder={tab === 'client' ? 'YOUR ACCESS CODE' : 'STUDIO KEY'}
          style={{ width:'100%', padding:'16px 20px', background:C.card, border:`1px solid ${err ? '#e55' : C.border}`, borderRadius:16, color:C.white, fontSize:13, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.35em', textAlign:'center', outline:'none', marginBottom:err?8:16, transition:'border .2s' }}
        />
        {err && <p style={{ color:'#e55', fontSize:11, marginBottom:12, textAlign:'center' }}>{err}</p>}

        <button onClick={handleSubmit}
          style={{ width:'100%', padding:'16px 0', background:C.gold, border:'none', borderRadius:16, color:C.bg, fontSize:11, fontFamily:'Montserrat,sans-serif', fontWeight:800, letterSpacing:'0.45em', textTransform:'uppercase', cursor:'pointer', transition:'all .2s' }}
          onMouseEnter={e => e.target.style.background=C.goldHov}
          onMouseLeave={e => e.target.style.background=C.gold}>
          {tab === 'client' ? 'Open My Gallery' : 'Enter Studio'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CLIENT VIEW
// ══════════════════════════════════════════════════════════════════════════════
function ClientView({ gallery: initial, code }) {
  const [gallery] = useState(initial);

  const statusMeta = {
    ready:   { label:'Ready',    color:'#22c55e', bg:'#052311' },
    editing: { label:'Editing',  color:C.gold,    bg:'#1a1200' },
    pending: { label:'Coming',   color:C.textSub, bg:C.card    },
  };

  const typeIcon = (type) => {
    if (type === 'photos') return <Images size={22} />;
    if (type === 'video')  return <Film size={22} />;
    return <Link size={22} />;
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:'Montserrat,sans-serif' }}>
      <style>{GLOBAL_STYLES}</style>

      {/* ── HERO ── */}
      <div style={{ position:'relative', height:'65vh', minHeight:420, overflow:'hidden' }}>
        {gallery.heroImage
          ? <img src={gallery.heroImage} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', filter:'brightness(.45) saturate(.8)' }} />
          : <div style={{ position:'absolute', inset:0, background:`linear-gradient(135deg, #1a1200 0%, #0a0a0a 100%)` }} />
        }
        {/* gradient overlay */}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, rgba(10,10,10,.3) 0%, rgba(10,10,10,.9) 100%)' }} />

        {/* content */}
        <div style={{ position:'relative', zIndex:2, height:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:'0 32px 52px', maxWidth:960, margin:'0 auto' }}>
          <div className="fade-up" style={{ marginBottom:20 }}>
            <img src={LOGO_URL} alt="Spark Studios" style={{ height:72, filter:'brightness(2)' }} />
          </div>
          <h1 className="font-serif fade-up-2" style={{ fontSize:'clamp(40px,8vw,80px)', fontWeight:300, color:C.white, lineHeight:1, marginBottom:12, fontStyle:'italic' }}>
            {gallery.clientName || 'Your Gallery'}
          </h1>
          {gallery.weddingDate && (
            <p className="fade-up-3" style={{ color:C.gold, fontSize:11, fontWeight:800, letterSpacing:'0.5em', textTransform:'uppercase' }}>
              {gallery.weddingDate}
            </p>
          )}
        </div>
      </div>

      {/* ── COVER NOTE ── */}
      {gallery.coverNote && (
        <div style={{ maxWidth:760, margin:'0 auto', padding:'48px 32px 0' }}>
          <div className="fade-up" style={{ borderLeft:`2px solid ${C.gold}`, paddingLeft:24 }}>
            <p className="font-serif" style={{ fontSize:'clamp(18px,2.5vw,24px)', color:'#d0ccc4', fontStyle:'italic', lineHeight:1.7, fontWeight:300 }}>
              "{gallery.coverNote}"
            </p>
            <p style={{ color:C.gold, fontSize:10, fontWeight:800, letterSpacing:'0.4em', textTransform:'uppercase', marginTop:16 }}>— Waqar · The Spark Studios</p>
          </div>
        </div>
      )}

      {/* ── SECTIONS ── */}
      <div style={{ maxWidth:960, margin:'0 auto', padding:'48px 32px 80px' }}>
        <div style={{ marginBottom:40 }}>
          <p style={{ color:C.textSub, fontSize:10, fontWeight:800, letterSpacing:'0.5em', textTransform:'uppercase', marginBottom:8 }}>Your Deliverables</p>
          <h2 className="font-serif" style={{ fontSize:'clamp(28px,4vw,44px)', color:C.white, fontWeight:300, fontStyle:'italic', lineHeight:1 }}>Everything, in one place.</h2>
        </div>

        <div style={{ display:'grid', gap:16 }}>
          {(gallery.sections || []).map((sec, i) => {
            const meta = statusMeta[sec.status] || statusMeta.pending;
            const isReady = sec.status === 'ready';
            return (
              <div key={sec.id} className={`fade-up`} style={{ animationDelay:`${i*0.08}s` }}>
                <div
                  style={{
                    background: C.card,
                    border: `1px solid ${isReady ? C.gold+'44' : C.border}`,
                    borderRadius: 24,
                    padding: '28px 32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 20,
                    transition: 'all .3s',
                    cursor: isReady && sec.url ? 'pointer' : 'default',
                    flexWrap: 'wrap',
                  }}
                  onClick={() => isReady && sec.url && window.open(sec.url, '_blank')}
                  onMouseEnter={e => { if (isReady && sec.url) { e.currentTarget.style.borderColor=C.gold; e.currentTarget.style.background='#1c1a14'; }}}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = isReady ? C.gold+'44' : C.border; e.currentTarget.style.background=C.card; }}
                >
                  {/* Icon + info */}
                  <div style={{ display:'flex', alignItems:'center', gap:20 }}>
                    <div style={{
                      width:52, height:52, borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                      background: isReady ? C.gold+'22' : C.surface,
                      color: isReady ? C.gold : C.textSub,
                      border: `1px solid ${isReady ? C.gold+'33' : C.border}`
                    }}>
                      {typeIcon(sec.type)}
                    </div>
                    <div>
                      <h3 style={{ color: isReady ? C.white : C.textSub, fontSize:16, fontWeight:700, marginBottom:4, letterSpacing:'.01em' }}>{sec.label}</h3>
                      {sec.note && <p style={{ color:C.textSub, fontSize:11, letterSpacing:'0.1em' }}>{sec.note}</p>}
                    </div>
                  </div>

                  {/* Status + action */}
                  <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                    <span style={{ background:meta.bg, color:meta.color, fontSize:10, fontWeight:800, letterSpacing:'0.35em', textTransform:'uppercase', padding:'6px 14px', borderRadius:999 }}>
                      {meta.label}
                    </span>
                    {isReady && sec.url && (
                      <div style={{ width:40, height:40, borderRadius:12, background:C.gold+'22', display:'flex', alignItems:'center', justifyContent:'center', color:C.gold }}>
                        <ExternalLink size={16} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── FOOTER CTA ── */}
        <div style={{ marginTop:64, textAlign:'center', padding:'48px 32px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:32 }}>
          <Sparkles size={28} style={{ color:C.gold, marginBottom:16 }} />
          <h3 className="font-serif" style={{ fontSize:28, color:C.white, fontStyle:'italic', fontWeight:300, marginBottom:8 }}>Questions about your gallery?</h3>
          <p style={{ color:C.textSub, fontSize:12, marginBottom:28, letterSpacing:'0.1em' }}>We're always here — reach out on WhatsApp anytime.</p>
          <button
            onClick={() => window.open(`https://wa.me/${WHATSAPP}?text=Hi Waqar! I have a question about my gallery (${gallery.clientName}).`, '_blank')}
            style={{ background:C.gold, border:'none', borderRadius:14, padding:'14px 32px', color:C.bg, fontSize:11, fontWeight:800, letterSpacing:'0.35em', textTransform:'uppercase', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8 }}>
            <MessageCircle size={16} /> Message Waqar
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ADMIN DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function AdminDashboard({ onEdit }) {
  const [galleries, setGalleries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [deleting,  setDeleting]  = useState(null);
  const [toast,     setToast]     = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'artifacts', APP_ID, 'public', 'data', 'galleries'),
      snap => {
        setGalleries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const createNew = async () => {
    const name = window.prompt('Client name for this gallery (e.g. "Sarah & James"):');
    if (name === null) return;
    const trimmedName = name.trim();

    const slug = trimmedName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'client';

    let id = slug;
    for (let attempt = 0; attempt < 5; attempt++) {
      // eslint-disable-next-line no-await-in-loop
      const existing = await getDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', id));
      if (!existing.exists()) break;
      id = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
    }

    const g = blankGallery();
    g.clientName = trimmedName;
    g.accessCode = id;
    await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', id), g);
    onEdit(g, id);
  };

  const deleteGallery = async (id) => {
    await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', id));
    setDeleting(null);
    showToast('Gallery deleted.');
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#/portal/${id}`;
    navigator.clipboard.writeText(url);
    showToast('Link copied!');
  };

  const filtered = galleries
    .filter(g => (g.clientName||'').toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => (b.createdAt||0)-(a.createdAt||0));

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:'Montserrat,sans-serif' }}>
      <style>{GLOBAL_STYLES}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:24, left:'50%', transform:'translateX(-50%)', background:C.gold, color:C.bg, padding:'12px 24px', borderRadius:999, fontSize:12, fontWeight:700, letterSpacing:'0.2em', zIndex:999, boxShadow:'0 8px 32px rgba(0,0,0,.5)' }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ borderBottom:`1px solid ${C.border}`, padding:'24px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:20 }}>
          <img src={LOGO_URL} alt="" style={{ height:28, filter:'brightness(2)' }} />
          <div>
            <h1 style={{ color:C.white, fontSize:18, fontWeight:700, letterSpacing:'0.02em' }}>Client Portals</h1>
            <p style={{ color:C.textSub, fontSize:10, fontWeight:700, letterSpacing:'0.4em', textTransform:'uppercase' }}>Studio Admin</p>
          </div>
        </div>
        <button onClick={createNew}
          style={{ background:C.gold, border:'none', borderRadius:14, padding:'12px 24px', color:C.bg, fontSize:11, fontWeight:800, letterSpacing:'0.35em', textTransform:'uppercase', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
          <Plus size={16} /> New Gallery
        </button>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'32px 32px' }}>
        {/* Search */}
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search client name…"
          style={{ width:'100%', maxWidth:400, background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'12px 18px', color:C.white, fontSize:13, fontFamily:'Montserrat,sans-serif', outline:'none', marginBottom:28 }}
        />

        {loading && <p style={{ color:C.textSub, textAlign:'center', padding:40 }}>Loading galleries…</p>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:80 }}>
            <Images size={40} style={{ color:C.muted, marginBottom:16 }} />
            <p style={{ color:C.textSub, fontSize:13 }}>No galleries yet. Create your first one.</p>
          </div>
        )}

        <div style={{ display:'grid', gap:12 }}>
          {filtered.map(g => (
            <div key={g.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:20, padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
              {/* Info */}
              <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                {g.heroImage
                  ? <img src={g.heroImage} alt="" style={{ width:52, height:52, borderRadius:12, objectFit:'cover', border:`1px solid ${C.border}` }} />
                  : <div style={{ width:52, height:52, borderRadius:12, background:C.surface, display:'flex', alignItems:'center', justifyContent:'center', color:C.muted }}><Images size={20} /></div>
                }
                <div>
                  <h3 style={{ color:C.white, fontSize:16, fontWeight:700, marginBottom:4 }}>{g.clientName || 'Unnamed Client'}</h3>
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                    {g.weddingDate && <span style={{ color:C.gold, fontSize:10, fontWeight:700, letterSpacing:'0.3em', textTransform:'uppercase' }}>{g.weddingDate}</span>}
                    <span style={{ color:C.textSub, fontSize:10, fontWeight:600 }}><Eye size={11} style={{ display:'inline', marginRight:4 }} />{g.views||0} views</span>
                    <span style={{ color:C.textSub, fontSize:10, fontWeight:600 }}><Clock size={11} style={{ display:'inline', marginRight:4 }} />Last seen {timeAgo(g.lastViewedAt)}</span>
                    <span style={{ color:C.textSub, fontSize:10, fontWeight:600 }}>Code: <code style={{ color:C.gold }}>{g.id}</code></span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display:'flex', gap:8, flexShrink:0, flexWrap:'wrap' }}>
                <ActionBtn icon={<Link size={14}/>} label="Copy Link" onClick={() => copyLink(g.id)} />
                <ActionBtn icon={<ExternalLink size={14}/>} label="Preview" onClick={() => window.open(`${window.location.origin}${window.location.pathname}#/portal/${g.id}`, '_blank')} />
                <ActionBtn icon={<Edit3 size={14}/>} label="Edit" gold onClick={() => onEdit(g, g.id)} />
                {deleting === g.id
                  ? <div style={{ display:'flex', gap:6 }}>
                      <ActionBtn icon={<Check size={14}/>} label="Confirm" danger onClick={() => deleteGallery(g.id)} />
                      <ActionBtn icon={<X size={14}/>}     label="Cancel" onClick={() => setDeleting(null)} />
                    </div>
                  : <ActionBtn icon={<Trash2 size={14}/>} label="Delete" danger onClick={() => setDeleting(g.id)} />
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SMALL ACTION BUTTON ───────────────────────────────────────────────────────
function ActionBtn({ icon, label, onClick, gold, danger }) {
  const bg = gold ? C.gold : danger ? '#2a0808' : C.surface;
  const color = gold ? C.bg : danger ? '#e55' : C.textSub;
  const border = gold ? 'none' : danger ? '1px solid #3a1212' : `1px solid ${C.border}`;
  return (
    <button onClick={onClick} title={label}
      style={{ background:bg, border, borderRadius:10, padding:'10px 14px', color, fontSize:10, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em', textTransform:'uppercase', cursor:'pointer', display:'flex', alignItems:'center', gap:6, transition:'opacity .2s' }}
      onMouseEnter={e => e.currentTarget.style.opacity='.8'}
      onMouseLeave={e => e.currentTarget.style.opacity='1'}>
      {icon} <span style={{ display:'none' }}>{label}</span>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  GALLERY EDITOR
// ══════════════════════════════════════════════════════════════════════════════
function GalleryEditor({ galleryId, initial, onBack, onSaved }) {
  const [form,    setForm]    = useState(initial || blankGallery());
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [toast,   setToast]   = useState('');
  const [uploading, setUploading] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const updateField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isHeic(file)) {
      alert('That looks like a HEIC/HEIF photo (the iPhone default format), which browsers can\'t process. Please convert it to JPG first — on iPhone: Settings > Camera > Formats > Most Compatible — then upload again.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const path = `galleries/${galleryId}/hero-${Date.now()}.jpg`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, resized, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(fileRef);
      updateField('heroImage', url);
      showToast('Photo uploaded!');
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const updateSection = (id, k, v) => setForm(p => ({ ...p, sections: p.sections.map(s => s.id===id ? {...s,[k]:v} : s) }));
  const addSection = () => setForm(p => ({ ...p, sections: [...p.sections, { id:Date.now(), type:'photos', label:'New Section', url:'', note:'', status:'pending' }] }));
  const removeSection = (id) => setForm(p => ({ ...p, sections: p.sections.filter(s => s.id!==id) }));

  const save = async () => {
    setSaving(true);
    const payload = { ...form, updatedAt: Date.now() };
    await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'galleries', galleryId), payload);
    setSaving(false);
    setSaved(true);
    onSaved(payload);
    setTimeout(() => setSaved(false), 3000);
    showToast('Saved!');
  };

  const portalUrl = `${window.location.origin}${window.location.pathname}#/portal/${galleryId}`;

  const inputStyle = (extra={}) => ({
    width: '100%',
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    color: C.white,
    fontSize: 13,
    fontFamily: 'Montserrat,sans-serif',
    outline: 'none',
    ...extra
  });

  const labelStyle = {
    color: C.textSub,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.4em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: 8
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:'Montserrat,sans-serif' }}>
      <style>{GLOBAL_STYLES}</style>

      {toast && (
        <div style={{ position:'fixed', top:24, left:'50%', transform:'translateX(-50%)', background:C.gold, color:C.bg, padding:'12px 24px', borderRadius:999, fontSize:12, fontWeight:700, letterSpacing:'0.2em', zIndex:999, boxShadow:'0 8px 32px rgba(0,0,0,.5)' }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ borderBottom:`1px solid ${C.border}`, padding:'20px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap', position:'sticky', top:0, background:C.bg, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={onBack} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.textSub, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:11, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em' }}>
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 style={{ color:C.white, fontSize:16, fontWeight:700 }}>{form.clientName || 'New Gallery'}</h1>
            <p style={{ color:C.textSub, fontSize:10, fontWeight:700, letterSpacing:'0.3em', textTransform:'uppercase' }}>Editing Gallery</p>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={() => { navigator.clipboard.writeText(portalUrl); showToast('Link copied!'); }}
            style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'11px 18px', color:C.textSub, cursor:'pointer', fontSize:11, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em', display:'flex', alignItems:'center', gap:6 }}>
            <Link size={14} /> Copy Link
          </button>
          <button onClick={() => window.open(portalUrl, '_blank')}
            style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'11px 18px', color:C.textSub, cursor:'pointer', fontSize:11, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em', display:'flex', alignItems:'center', gap:6 }}>
            <Eye size={14} /> Preview
          </button>
          <button onClick={save} disabled={saving}
            style={{ background: saved ? '#052311' : C.gold, border: saved ? '1px solid #22c55e' : 'none', borderRadius:12, padding:'11px 24px', color: saved ? '#22c55e' : C.bg, cursor:'pointer', fontSize:11, fontFamily:'Montserrat,sans-serif', fontWeight:800, letterSpacing:'0.35em', textTransform:'uppercase', display:'flex', alignItems:'center', gap:8, transition:'all .3s' }}>
            {saving ? <><span className="spin" style={{ display:'inline-block', width:14, height:14, border:'2px solid', borderTopColor:'transparent', borderRadius:'50%' }} /> Saving…</> : saved ? <><Check size={14}/> Saved!</> : <><Save size={14}/> Save</>}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'40px 32px 80px', display:'grid', gap:32 }}>

        {/* Access code info */}
        <div style={{ background:C.surface, border:`1px solid ${C.gold}33`, borderRadius:20, padding:'20px 24px' }}>
          <p style={labelStyle}>Client Access Code</p>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <code style={{ color:C.gold, fontSize:16, fontWeight:800, letterSpacing:'0.2em', background:C.card, padding:'8px 16px', borderRadius:10, border:`1px solid ${C.border}` }}>{galleryId}</code>
            <p style={{ color:C.textSub, fontSize:11 }}>Share this code (or the portal link) with your client.</p>
          </div>
        </div>

        {/* Client Details */}
        <Section title="Client Details" icon={<Camera size={16}/>}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <Field label="Client Name (displayed on portal)">
              <input style={inputStyle()} value={form.clientName} onChange={e => updateField('clientName', e.target.value)} placeholder="e.g. Sarah & James" />
            </Field>
            <Field label="Wedding Date">
              <input style={inputStyle()} value={form.weddingDate} onChange={e => updateField('weddingDate', e.target.value)} placeholder="e.g. September 14, 2025" />
            </Field>
          </div>
          <Field label="Hero Image (banner photo shown to the client)" style={{ marginTop:20 }}>
            <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              {form.heroImage && (
                <img src={form.heroImage} alt="" style={{ width:64, height:64, borderRadius:12, objectFit:'cover', border:`1px solid ${C.border}` }} />
              )}
              <label style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'12px 18px', color:C.gold, fontSize:11, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', cursor: uploading ? 'default' : 'pointer', display:'inline-flex', alignItems:'center', gap:8, opacity: uploading ? .6 : 1 }}>
                {uploading
                  ? <><span className="spin" style={{ display:'inline-block', width:12, height:12, border:'2px solid', borderTopColor:'transparent', borderRadius:'50%' }} /> Uploading…</>
                  : <><Camera size={14} /> Upload Photo</>}
                <input type="file" accept="image/*" onChange={handleHeroUpload} disabled={uploading} style={{ display:'none' }} />
              </label>
            </div>
            <input style={inputStyle({ marginTop:10 })} value={form.heroImage} onChange={e => updateField('heroImage', e.target.value)} placeholder="or paste an image URL directly" />
          </Field>
          <Field label="Personal Note to Client (shown on portal)" style={{ marginTop:20 }}>
            <textarea style={inputStyle({ resize:'vertical', minHeight:80 })} value={form.coverNote} onChange={e => updateField('coverNote', e.target.value)} placeholder="A short personal message, e.g. It was an honour to document your story…" />
          </Field>
        </Section>

        {/* Deliverable Sections */}
        <Section title="Deliverable Sections" icon={<Film size={16}/>}
          action={<button onClick={addSection} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'8px 16px', color:C.gold, cursor:'pointer', fontSize:10, fontFamily:'Montserrat,sans-serif', fontWeight:700, letterSpacing:'0.3em', textTransform:'uppercase', display:'flex', alignItems:'center', gap:6 }}><Plus size={12}/> Add Section</button>}>

          <div style={{ display:'grid', gap:16 }}>
            {form.sections.map((sec, i) => (
              <div key={sec.id} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:16, padding:'20px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <span style={{ color:C.gold, fontSize:10, fontWeight:800, letterSpacing:'0.4em' }}>Section {i+1}</span>
                  <button onClick={() => removeSection(sec.id)} style={{ background:'transparent', border:'none', color:'#666', cursor:'pointer', padding:4 }}><Trash2 size={14}/></button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
                  <Field label="Label">
                    <input style={inputStyle()} value={sec.label} onChange={e => updateSection(sec.id,'label',e.target.value)} placeholder="Wedding Photos" />
                  </Field>
                  <Field label="Type">
                    <select style={inputStyle()} value={sec.type} onChange={e => updateSection(sec.id,'type',e.target.value)}>
                      <option value="photos">Photos</option>
                      <option value="video">Video</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Status">
                    <select style={inputStyle()} value={sec.status} onChange={e => updateSection(sec.id,'status',e.target.value)}>
                      <option value="ready">Ready</option>
                      <option value="editing">Editing</option>
                      <option value="pending">Coming Soon</option>
                    </select>
                  </Field>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
                  <Field label="Synology / Gallery URL">
                    <input style={inputStyle()} value={sec.url} onChange={e => updateSection(sec.id,'url',e.target.value)} placeholder="https://… (Synology share link)" />
                  </Field>
                  <Field label="Note (optional)">
                    <input style={inputStyle()} value={sec.note} onChange={e => updateSection(sec.id,'note',e.target.value)} placeholder="e.g. Password protected" />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Section>

      </div>
    </div>
  );
}

// ── SECTION WRAPPER ───────────────────────────────────────────────────────────
function Section({ title, icon, children, action }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:24, overflow:'hidden' }}>
      <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ color:C.gold }}>{icon}</span>
          <h2 style={{ color:C.white, fontSize:14, fontWeight:700, letterSpacing:'0.05em' }}>{title}</h2>
        </div>
        {action}
      </div>
      <div style={{ padding:'24px' }}>{children}</div>
    </div>
  );
}

// ── FORM FIELD ────────────────────────────────────────────────────────────────
function Field({ label, children, style }) {
  return (
    <div style={style}>
      <label style={{ color:'#666', fontSize:10, fontWeight:700, letterSpacing:'0.4em', textTransform:'uppercase', display:'block', marginBottom:7 }}>{label}</label>
      {children}
    </div>
  );
}
